"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { deriveLocaleFromPath } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

/**
 * Bottom privacy / cookie consent bar.
 *
 * Fixed + high z-index so SiteAtmosphere cannot cover it. Critical colors use
 * inline styles (same lesson as AnnouncementBanner). Choice is persisted in
 * localStorage; banner only mounts after client read to avoid hydration mismatch.
 */
const STORAGE_KEY = "cloudaxe-privacy-consent";

type ConsentChoice = "accepted" | "declined";

function readStoredConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "accepted" || value === "declined") return value;
    return null;
  } catch (error) {
    console.error("PrivacyConsentBanner: failed to read consent", error);
    return null;
  }
}

function writeStoredConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch (error) {
    console.error("PrivacyConsentBanner: failed to save consent", error);
  }
}

export function PrivacyConsentBanner() {
  const [visible, setVisible] = useState(false);
  const pathname = usePathname() || "/";
  const { locale } = deriveLocaleFromPath(pathname);
  const m = getMessages(locale);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) setVisible(true);
  }, []);

  const choose = (choice: ConsentChoice) => {
    writeStoredConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={m.privacy.aria}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9998,
        padding: "12px 16px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          margin: "0 auto",
          maxWidth: 1120,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px 20px",
          padding: "14px 18px",
          borderRadius: 14,
          color: "#f8fafc",
          background: "rgba(15, 23, 42, 0.96)",
          border: "1px solid rgba(148, 163, 184, 0.28)",
          boxShadow: "0 12px 40px rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(10px)",
        }}
      >
        <p
          style={{
            margin: 0,
            flex: "1 1 280px",
            fontSize: "0.875rem",
            lineHeight: 1.45,
            color: "#e2e8f0",
          }}
        >
          {m.privacy.body}{" "}
          <Link href="/privacy" style={{ color: "#7dd3fc", textDecoration: "underline" }}>
            {m.privacy.policy}
          </Link>
          .
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            flex: "0 0 auto",
          }}
        >
          <button
            type="button"
            onClick={() => choose("declined")}
            style={{
              appearance: "none",
              cursor: "pointer",
              borderRadius: 10,
              border: "1px solid rgba(148, 163, 184, 0.45)",
              background: "transparent",
              color: "#e2e8f0",
              fontSize: "0.875rem",
              fontWeight: 500,
              padding: "8px 14px",
            }}
          >
            {m.privacy.decline}
          </button>
          <button
            type="button"
            onClick={() => choose("accepted")}
            style={{
              appearance: "none",
              cursor: "pointer",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(90deg, #0891b2 0%, #0284c7 50%, #1d4ed8 100%)",
              color: "#ffffff",
              fontSize: "0.875rem",
              fontWeight: 600,
              padding: "8px 16px",
            }}
          >
            {m.privacy.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
